import { Outlet, useLocation } from 'react-router-dom';
import Sidebar from './Sidebar';
import RecentActivity from './RecentActivity.tsx';
import MobileNav from './MobileNav';
import MobileHeader from './MobileHeader';
import { AddItemModal } from '../ui/add-item-modal';
import { GlobalImageRecognitionModal } from '../ui/global-image-recognition-modal';
import GlobalNutritionAnalysisModal from '../ui/global-nutrition-analysis-modal';
import { GlobalScanResultsModal } from '../ui/global-scan-results-modal';
import { GlobalRecipeGenerationModal } from '../ui/global-recipe-generation-modal';
import { GlobalRecipeResultModal } from '../ui/global-recipe-result-modal';
// import { useMobileDebugConsole } from '../ui/mobile-debug-console';
import { useEffect, useState, useCallback } from 'react';
import { Plus, Filter, Settings, Grid, List, Calendar, ShoppingBag, LogOut } from 'lucide-react';
import { Button } from '../ui/button';
import { useGlobalImageScanner } from '../../lib/global-image-scan';
import { useNoshStore } from '../../lib/store';

const Layout = () => {
  const [isMobile, setIsMobile] = useState(window.innerWidth < 768);
  const [showAddItemModal, setShowAddItemModal] = useState(false);
  const location = useLocation();
  // const { DebugConsole } = useMobileDebugConsole();
  
  // Global scan handling
  const { isScanning, startScan } = useGlobalImageScanner();
  const { showNutritionAnalysis, showScanResults } = useNoshStore();

  useEffect(() => {
    const handleResize = () => {
      setIsMobile(window.innerWidth < 768);
    };

    window.addEventListener('resize', handleResize);
    
    // Listen for custom event from MobileNav "Add Manually" button
    const handleAddItemEvent = () => {
      setShowAddItemModal(true);
    };
    
    window.addEventListener('openAddItemModal', handleAddItemEvent);
    
    return () => {
      window.removeEventListener('resize', handleResize);
      window.removeEventListener('openAddItemModal', handleAddItemEvent);
    };
  }, []);

  // Global scan event handlers for mobile navigation  
  const handleMealScan = useCallback(async (event: Event) => {
    const customEvent = event as CustomEvent;
    const { file } = customEvent.detail;
    if (file) {
      console.log('🍽️ [Global Layout] Handling meal scan from mobile navigation:', {
        fileName: file.name,
        fileSize: Math.round(file.size / 1024) + 'KB',
        currentPage: location.pathname
      });

      try {
        await startScan(
          file, 
          'meal',
          // On completion callback
          (result) => {
            console.log('✅ [Global Layout] Meal scan completed:', result);
            
            // Only show nutrition modal if scan was successful
            if (result.success && result.data) {
              showNutritionAnalysis({
                mealData: result.data,
                imageUrl: result.imageUrl || '',
                imagePath: result.imagePath || '',
                mode: 'analysis'
              });
              
              console.log('🔗 [Global Layout] Meal scan result passed to global modal');
            } else {
              console.log('⚠️ [Global Layout] Scan failed or usage limit exceeded, not showing nutrition modal');
            }
          },
          // On error callback
          (error) => {
            console.error('❌ [Global Layout] Meal scan failed:', error);
          }
        );
      } catch (error: any) {
        console.error('❌ [Global Layout] Failed to start meal scan:', error);
      }
    }
  }, [startScan, showNutritionAnalysis, location.pathname]);

  const handleInventoryScan = useCallback(async (event: Event) => {
    const customEvent = event as CustomEvent;
    const { file } = customEvent.detail;
    if (file) {
      console.log('📦 [Global Layout] Handling inventory scan from mobile navigation:', {
        fileName: file.name,
        fileSize: Math.round(file.size / 1024) + 'KB',
        currentPage: location.pathname
      });

      try {
        await startScan(
          file, 
          'inventory',
          // On completion callback
          (result) => {
            console.log('✅ [Global Layout] Inventory scan completed:', result);
            
            // Show global scan results modal
            showScanResults({
              scanData: result.data,
              imageUrl: result.imageUrl || '',
              imagePath: result.imagePath || ''
            });
            
            console.log('🔗 [Global Layout] Inventory scan result passed to global modal');
          },
          // On error callback
          (error) => {
            console.error('❌ [Global Layout] Inventory scan failed:', error);
          }
        );
      } catch (error: any) {
        console.error('❌ [Global Layout] Failed to start inventory scan:', error);
      }
    }
  }, [startScan, showScanResults, location.pathname]);

  useEffect(() => {

    // Add global event listeners for mobile navigation scan events
    window.addEventListener('triggerMealScan', handleMealScan);
    window.addEventListener('triggerInventoryScan', handleInventoryScan);
    
    // Only log once per session to prevent spam
    if (!sessionStorage.getItem('layout_listeners_logged')) {
      console.log('🔗 [Global Layout] Global scan event listeners registered for all pages');
      sessionStorage.setItem('layout_listeners_logged', 'true');
    }
    
    return () => {
      window.removeEventListener('triggerMealScan', handleMealScan);
      window.removeEventListener('triggerInventoryScan', handleInventoryScan);
      // No removal log to prevent spam during development re-renders
    };
  }, [handleMealScan, handleInventoryScan]); // ✅ FIXED: Include callback dependencies

  // Apply global scroll behavior management
  useEffect(() => {
    // Add layout-active class to body for global scroll control
    document.body.classList.add('layout-active');
    
    return () => {
      document.body.classList.remove('layout-active');
    };
  }, []);

  // Enhanced scroll reset - more robust and universal
  useEffect(() => {
    // Multiple strategies to ensure scroll reset works universally
    const resetScroll = () => {
      // 1. Reset Layout's scroll container
      const contentContainer = document.querySelector('[data-scroll-container]');
      if (contentContainer) {
        contentContainer.scrollTop = 0;
      }
      
      // 2. Reset window scroll as fallback
      window.scrollTo(0, 0);
      
      // 3. Reset any other potential scroll containers
      const allScrollContainers = document.querySelectorAll('.overflow-y-auto, .overflow-auto');
      allScrollContainers.forEach(container => {
        if (container.scrollTop !== undefined) {
          container.scrollTop = 0;
        }
      });
      
      // 4. Force scroll reset with requestAnimationFrame for timing
      requestAnimationFrame(() => {
        if (contentContainer) {
          contentContainer.scrollTop = 0;
        }
        window.scrollTo(0, 0);
      });
    };

    // Reset immediately
    resetScroll();
    
    // Also reset after a short delay to catch any delayed content loading
    const timeoutId = setTimeout(resetScroll, 100);
    
    return () => clearTimeout(timeoutId);
  }, [location.pathname]);

  // Handle logout function
  const handleLogout = async () => {
    try {
      const { handleLogoutRedirect } = await import('../../lib/logout-utils');
      await handleLogoutRedirect();
    } catch (error) {
      console.error('Error logging out:', error);
      // Fallback: redirect using utility
      const { getLogoutRedirectUrl } = await import('../../lib/logout-utils');
      window.location.href = getLogoutRedirectUrl();
    }
  };

  // Get page-specific header props
  const getHeaderProps = () => {
    switch (location.pathname) {
      case '/dashboard':
        // Return null to hide header - Dashboard will handle its own swipeable header
        return null;
      case '/inventory':
        // Return null for mobile to hide the header completely
        return null;
      case '/recipes':
        return null;
      case '/nutrition-analysis':
        return null;
      case '/profile':
        // Hide header on mobile to avoid redundancy with profile card
        return isMobile ? null : {
          variant: 'profile' as const,
          title: "My Profile",
          subtitle: "Manage your account and preferences",
          showAvatar: true,
          rightAction: (
            <div className="flex items-center space-x-2">
              <Button variant="ghost" size="icon" className="h-8 w-8 rounded-full bg-muted hover:bg-muted/80">
                <Settings size={18} />
              </Button>
              <Button 
                variant="ghost" 
                size="icon" 
                className="h-8 w-8 rounded-full bg-red-50 hover:bg-red-100 text-red-600 hover:text-red-700"
                onClick={handleLogout}
                title="Logout"
              >
                <LogOut size={18} />
              </Button>
            </div>
          )
        };
      case '/shopping':
        // Hide header on mobile to avoid redundancy with shopping list's own mobile design
        return isMobile ? null : {
          variant: 'page' as const,
          title: "Shopping List",
          subtitle: "Items you need to buy",
          showSearch: true,
          rightAction: (
            <>
              <Button variant="ghost" size="icon" className="h-8 w-8 rounded-full bg-muted hover:bg-muted/80">
                <ShoppingBag size={18} />
              </Button>
              <Button variant="ghost" size="icon" className="h-8 w-8 rounded-full bg-primary/10 hover:bg-primary/20 text-primary">
                <Plus size={18} />
              </Button>
            </>
          )
        };
      case '/tracker':
        // Hide header on mobile to avoid redundancy with meal tracker's own mobile design
        return isMobile ? null : {
          variant: 'page' as const,
          title: "Meal Tracker",
          subtitle: "Track your daily meals",
          rightAction: (
            <Button variant="ghost" size="icon" className="h-8 w-8 rounded-full bg-primary/10 hover:bg-primary/20 text-primary">
              <Plus size={18} />
            </Button>
          )
        };
      case '/planner':
        // Hide header on mobile to avoid redundancy with planner's own mobile design
        return isMobile ? null : {
          variant: 'page' as const,
          title: "Weekly Planner",
          subtitle: "Plan your meals for the week",
          rightAction: (
            <>
              <Button variant="ghost" size="icon" className="h-8 w-8 rounded-full bg-muted hover:bg-muted/80">
                <Calendar size={18} />
              </Button>
              <Button variant="ghost" size="icon" className="h-8 w-8 rounded-full bg-primary/10 hover:bg-primary/20 text-primary">
                <Plus size={18} />
              </Button>
            </>
          )
        };
      default:
        return {
          variant: 'page' as const,
          title: "Nosh",
          showSearch: true
        };
    }
  };

  // Helper function to determine top padding
  const getTopPadding = () => {
    // Desktop: always add top padding for page content
    if (!isMobile) {
      return 'pt-6 md:pt-8';
    }
    
    // Mobile: adjust padding based on page type
    if (location.pathname === '/inventory' || location.pathname === '/nutrition-analysis' || location.pathname === '/dashboard' || location.pathname === '/recipes' || location.pathname === '/tracker') {
      return 'pt-0'; // These pages handle their own headers
    }
    
    // Other mobile pages get minimal top padding since header is outside scroll container
    return 'pt-4';
  };

  return (
    <div className="flex min-h-screen bg-background">
      {!isMobile && <Sidebar />}
      <main className={`flex-1 mr-0 md:mr-60 scrollbar-hide overflow-hidden ${isMobile ? 'pb-20' : ''}`}>
        <div 
          key={location.pathname} // Force re-render with new scroll container per route
          data-scroll-container // Add identifier for scroll reset
          className="scrollbar-hide"
          style={{
            height: isMobile 
              ? 'calc(100vh - 100px)'  // Just navigation + padding for mobile
              : 'calc(100vh - 16px)', // Desktop: small margin for browser chrome
            overflowY: 'auto',
            overscrollBehavior: 'contain' // Prevent scroll chaining
          }}
        >
          {/* Mobile Header - moved inside scroll container so it scrolls with content */}
          {isMobile && getHeaderProps() && (
            <div className="px-4 pt-6">
              <MobileHeader {...getHeaderProps()} />
            </div>
          )}
          
          <div className={`page-content ${isMobile ? 'px-4' : 'pl-5 pr-7 md:pl-4 md:pr-14'} ${getTopPadding()}`}>
            <Outlet />
          </div>
        </div>
      </main>
      {!isMobile && <RecentActivity />}
      {isMobile && <MobileNav />}
      
      {/* Global Add Item Modal */}
      <AddItemModal 
        isOpen={showAddItemModal} 
        onClose={() => setShowAddItemModal(false)} 
      />

      {/* Global Image Recognition Progress Modal */}
      <GlobalImageRecognitionModal />
      
      {/* Global Nutrition Analysis Modal */}
      <GlobalNutritionAnalysisModal />
      
      {/* Global Scan Results Modal */}
      <GlobalScanResultsModal />

      {/* Global Recipe Generation Progress Modal */}
      <GlobalRecipeGenerationModal />
      
      {/* Global Recipe Result Modal */}
      <GlobalRecipeResultModal />

      {/* Debug Console - Mobile debugging tool (commented out to reduce console spam) */}
      {/* <DebugConsole /> */}
    </div>
  );
};

export default Layout;